import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";
import { getUserData, getHearderAuth } from "@utils";

const StorageUsageComponent = () => {
  const location = useLocation();
  const [data, setData] = useState({ used: 0, available: 0 });
  const [retry, setRetry] = useState(false);

  const [modal, setModal] = useState(false);

  const toggle = () => setModal(!modal);

  useEffect(() => {
    const authToken = getUserData()?.data?.drive || "";

    const fetchData = async () => {
      if (!authToken) return;
      try {
        const result = await axios({
          method: "get",
          url: "https://drive.cloodo.com/api/v1/user/space-usage",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (result.data && result.data.status === "success") {
          setData({
            used: result.data.used,
            available: result.data.available,
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 403 && !retry) {
          refreshToken();
        } else {
          console.error("Error fetching data: ", error);
        }
      }
    };

    const refreshToken = async () => {
      try {
        const refreshResult = await axios({
          method: "post",
          url: "https://convert.cloodo.com/synch-drive",
          headers: getHearderAuth(),
          data: { synch: "drive" },
        });

        const newToken = refreshResult.data.newToken;
        // Cập nhật token mới vào localStorage
        const userData = getUserData();
        if (userData) {
          userData.data.drive = newToken;
          localStorage.setItem("userData", JSON.stringify(userData));
          setRetry(true); // Đánh dấu đã retry để không lặp lại nếu gặp lỗi lần nữa
          fetchData(); // Gọi lại hàm fetchData với token mới
        }
      } catch (refreshError) {
        console.error("Error refreshing token: ", refreshError);
      }
    };

    fetchData();
  }, [retry]);

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Hàm để xác định màu sắc dựa trên phần trăm đã sử dụng
  const getProgressColor = (percentage) => {
    if (percentage < 50) return "#28a745"; // màu xanh lá cho dưới 50%
    if (percentage < 75) return "#ffc107"; // màu vàng cho từ 50% đến 75%
    return "#dc3545"; // màu đỏ cho trên 75%
  };

  const percentageUsed = (data.used / data.available) * 100;
  const progressColor = getProgressColor(percentageUsed);

  if (data.used === 0 || data.available === 0) {
    return null;
  }

  return (
    <>
      {location?.pathname?.includes("chat-file-storage") ? (
        <>
          <div
            className="p-2 d-flex"
            style={{ background: "#FFFFFF", borderRadius: "8px", alignItems:"center", justifyContent:"space-between" }}
          >
            <div className="ms-1" style={{fontWeight:"500", fontSize:"15px"}}>
              File storage capacity is synchronized in Message. When the allowed
              capacity is exceeded, file synchronization in Messages will
              temporarily stop.
            </div>
            <div
              id="el-tooltip-storage-usage"
              style={{
                minWidth: "300px",
                backgroundColor: "#2D58AF",
                borderRadius: "8px",
                padding: "5px",
                boxSizing: "border-box",
                margin: "auto",
                marginTop: "10px",
              }}
            >
              <div style={{padding:"10px 30px"}}>
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "2px",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      width: `${percentageUsed}%`,
                      backgroundColor: progressColor,
                      height: "10px",
                      borderRadius: "6px",
                      transition: "width 0.5s ease-in-out",
                    }}
                  ></div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginTop: "2px",
                    color: "#fff",
                  }}
                >
                  {`${formatBytes(data.used)} of ${formatBytes(
                    data.available
                  )} used`}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <Button.Ripple
            outline
            color="primary"
            size="sm"
            id="el-tooltip-storage-usage"
            onClick={toggle}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              viewBox="0 0 24 24"
            >
              <g fill="none" fill-rule="evenodd">
                <path d="M24 0v24H0V0zM12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036c-.01-.003-.019 0-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427c-.002-.01-.009-.017-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092c.012.004.023 0 .029-.008l.004-.014l-.034-.614c-.003-.012-.01-.02-.02-.022m-.715.002a.023.023 0 0 0-.027.006l-.006.014l-.034.614c0 .012.007.02.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z" />
                <path
                  fill="currentColor"
                  d="M5 4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 9a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2zm1 4a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1M7 7a1 1 0 0 0 0 2h2a1 1 0 0 0 0-2z"
                />
              </g>
            </svg>
          </Button.Ripple>
          <UncontrolledTooltip
            placement="top"
            target="el-tooltip-storage-usage"
          >
            {`${formatBytes(data.used)} of ${formatBytes(data.available)} used`}
          </UncontrolledTooltip>

          <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader toggle={toggle}>Storage information</ModalHeader>
            <ModalBody>
              File storage capacity is synchronized in Message. When the allowed
              capacity is exceeded, file synchronization in Messages will
              temporarily stop.
              <div
                id="el-tooltip-storage-usage"
                style={{
                  width: "185px",
                  backgroundColor: "#f0f1f3",
                  borderRadius: "8px",
                  padding: "5px",
                  boxSizing: "border-box",
                  margin: "auto",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "8px",
                    padding: "2px",
                  }}
                >
                  <div
                    style={{
                      width: `${percentageUsed}%`,
                      backgroundColor: progressColor,
                      height: "10px",
                      borderRadius: "6px",
                      transition: "width 0.5s ease-in-out",
                    }}
                  ></div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginTop: "2px",
                  }}
                >
                  {`${formatBytes(data.used)} of ${formatBytes(
                    data.available
                  )} used`}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggle}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </>
  );
};

export default StorageUsageComponent;
